import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api.js';
import { homeFor, pendingInvite, rememberOrganisation, useAuth } from '../auth.jsx';
import AuthLayout from '../components/AuthLayout.jsx';
import Alert from '../components/ui/Alert.jsx';
import { Loading } from '../components/ui/index.jsx';
import { fmtDate } from '../utils/format.js';
import { ROLE_INFO } from './company/Team.jsx';

const withArticle = (role) => ({ owner: 'an owner', admin: 'an admin', member: 'a member' }[role]);

/**
 * The page behind an emailed team invite. Opening it changes nothing (mail filters open
 * every link to scan it). Joining needs an account on exactly the invited address:
 * signed in → "Join"; new → sign up with that address (the code proves it).
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const auth = useAuth();
  const { user, loading, logout, refresh } = auth;
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    api(`/public/invites/${token}`).then((d) => setInvite(d.invite)).catch((err) => setError(err.message));
  }, [token]);

  if (error && !invite) return <AuthLayout><h1>Team invitation</h1><Alert tone="danger">{error}</Alert></AuthLayout>;
  if (!invite || loading) return <AuthLayout><Loading /></AuthLayout>;

  const remember = () => pendingInvite.set({ token, email: invite.email, organisation_name: invite.organisation_name });
  const signUp = () => { remember(); navigate('/register', { state: { invite: { token, email: invite.email, organisation_name: invite.organisation_name } } }); };
  const signIn = () => { remember(); navigate('/login', { state: { from: `/accept-invite/${token}`, email: invite.email } }); };
  const switchAccount = async () => { await logout(); signIn(); };

  const join = async () => {
    setBusy(true); setError('');
    try {
      const res = await api(`/portal/invites/${token}/accept`, { method: 'POST' });
      pendingInvite.clear();
      rememberOrganisation(res.organisation.id);
      await refresh();
      setJoined(res);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (joined) {
    return (
      <AuthLayout onSubmit={(e) => e.preventDefault()}>
        <h1>You&rsquo;ve joined {joined.organisation.name}</h1>
        <Alert tone="success">
          {joined.already_member ? `You were already in ${joined.organisation.name}, so nothing else changed.` : `You’re now ${withArticle(joined.role)} of ${joined.organisation.name}.`}
          {joined.domains_verified.length > 0 && <> Signing in with your address also completed the proof for <strong>{joined.domains_verified.join(', ')}</strong>, so {joined.domains_verified.length === 1 ? 'it is' : 'they are'} now verified.</>}
        </Alert>
        <button type="button" className="btn btn-primary btn-block" onClick={() => navigate(homeFor(auth), { replace: true })}>Go to {joined.organisation.name}</button>
      </AuthLayout>
    );
  }

  const role = ROLE_INFO[invite.role];
  const mine = user && user.email.toLowerCase() === invite.email;

  return (
    <AuthLayout onSubmit={(e) => { e.preventDefault(); if (mine && invite.status === 'pending') join(); }}>
      <h1>Join {invite.organisation_name} on SummitShield</h1>
      {error && <Alert tone="danger">{error}</Alert>}

      {invite.status === 'pending' && (
        <>
          <p><strong>{invite.invited_by || 'Someone'}</strong>{invite.invited_by_email && invite.invited_by !== invite.invited_by_email && <span className="muted"> ({invite.invited_by_email})</span>}{' '}
            has invited <span className="mono">{invite.email}</span> to join <strong>{invite.organisation_name}</strong> as <strong>{withArticle(invite.role)}</strong>.</p>
          <p className="muted small"><strong>What {withArticle(invite.role)} can do:</strong> {role.does}</p>
          <p className="muted small">SummitShield helps attendees and exhibitors check whether an email, website or payment request really comes from the organiser.</p>

          {mine && (
            <>
            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? 'Joining…' : `Join ${invite.organisation_name}`}</button>
            <p className="muted small">By joining you agree to our <Link to="/terms" target="_blank">terms</Link> and <Link to="/privacy" target="_blank">privacy policy</Link>.</p>
            </>
          )}
          {user && !mine && (
            <>
              <Alert tone="warn">
                You&rsquo;re signed in as <span className="mono">{user.email}</span>, but this invite is for <span className="mono">{invite.email}</span>.
                Only that address can accept it, so nobody else can join with a forwarded link.
              </Alert>
              <button type="button" className="btn btn-primary btn-block" onClick={switchAccount}>Sign out and sign in as {invite.email}</button>
            </>
          )}
          {!user && (
            <div className="decision-buttons">
              <button type="button" className="btn btn-primary btn-block" onClick={signIn}>I have an account — sign in</button>
              <button type="button" className="btn btn-secondary btn-block" onClick={signUp}>I&rsquo;m new — create an account</button>
            </div>
          )}
          <p className="muted small">The link works until {fmtDate(invite.expires_at)}, and only once. Use the address it was sent to: {invite.email}.</p>
        </>
      )}

      {invite.status === 'accepted' && (
        <Alert tone="info">This invite has already been used. {user ? <Link to="/">Go to SummitShield</Link> : <Link to="/login">Sign in</Link>} to carry on.</Alert>
      )}
      {invite.status === 'expired' && (
        <Alert tone="warn">This invite has expired, so it can&rsquo;t be used. Ask {invite.invited_by || 'whoever invited you'} to send it again from their Team page.</Alert>
      )}
      {invite.status === 'cancelled' && (
        <Alert tone="warn">This invite was cancelled. If you think that&rsquo;s a mistake, ask {invite.invited_by || 'whoever invited you'} to invite you again.</Alert>
      )}
      <p className="muted small">SummitShield will never ask you for a code, a password or a payment by phone or email.</p>
    </AuthLayout>
  );
}
